import { create } from 'zustand';
import { combine, subscribeWithSelector } from 'zustand/middleware';
import type { AudioMode, CameraFacing, Layout, SessionType } from '../types';
import { LAYOUT } from '../utils';

export type ConfigStateInternal = {
  devSessionId: string;
  internalSettings?: string;
  sessionId?: string;
  iAmRecorder: boolean;
  showFrameRate: boolean;
  enableCompanionMode: boolean;
};

export type ConfigStateMobile = {
  audioMode?: AudioMode['type'];
  hideAudioModeButton: boolean;
};

export type ConfigStateWeb = {
  enableNoiseReduction: boolean;
  audioInputDeviceId?: string;
  audioOutputDeviceId?: string;
  videoInputDeviceId?: string;
  hideVirtualBackgroundButton: boolean;
};

export type ConfigStateBoth = {
  sessionType: SessionType;
  layout: Layout;
  initialCameraFacing?: CameraFacing;
  autoStartRecording: boolean;
  hideRecordingButton: boolean;
  hideControlPanel: boolean;
  hideLeaveSessionButton: boolean;
  hideHeaderPanel: boolean;
  hideRaiseHandButton: boolean;
  hideShareInviteButton: boolean;
  hideChangeLayoutButton: boolean;
  hideToggleAudioButton: boolean;
  hideToggleVideoButton: boolean;
  hideParticipantListButton: boolean;
  hideChatButton: boolean;
  hideScreenSharingButton: boolean;
  hideSessionTimer: boolean;
  hideNetworkIndicator: boolean;
  hideRecordingStatusIndicator: boolean;
  hideSwitchCameraButton: boolean;
  enableParticipantContextMenu: boolean;
  // lowBandwidthMode: boolean;
  displayName: string;
  startAudioMuted: boolean;
  startVideoPaused: boolean;
  title: string;
  idleTimeoutPeriodBeforePrompt: number;
  idleTimeoutPeriodAfterPrompt: number;
  enableSpotlightDrag: boolean;
  enableSpotlightSwap: boolean;
  isPeerCall: boolean;
};

export type ConfigState = ConfigStateInternal &
  ConfigStateMobile &
  ConfigStateWeb &
  ConfigStateBoth;

type Actions = {
  reset: () => void;
};

const initialState: ConfigState = {
  sessionType: 'VIDEO',
  autoStartRecording: false,
  layout: LAYOUT.TILE,
  hideRecordingButton: true,
  hideVirtualBackgroundButton: false,
  hideAudioModeButton: false,
  hideSwitchCameraButton: false,
  devSessionId: 'dev-session',
  iAmRecorder: false,
  hideControlPanel: false,
  hideHeaderPanel: false,
  hideRaiseHandButton: false,
  hideShareInviteButton: true,
  hideChangeLayoutButton: false,
  hideLeaveSessionButton: false,
  hideToggleAudioButton: false,
  hideParticipantListButton: false,
  hideChatButton: true,
  hideToggleVideoButton: false,
  hideScreenSharingButton: false,
  hideSessionTimer: false,
  hideNetworkIndicator: true,
  hideRecordingStatusIndicator: false,
  enableParticipantContextMenu: true,
  // lowBandwidthMode: false,
  displayName: '',
  startAudioMuted: false,
  startVideoPaused: false,
  title: '',
  enableNoiseReduction: false,
  idleTimeoutPeriodBeforePrompt: 60000,
  idleTimeoutPeriodAfterPrompt: 180000,
  enableSpotlightDrag: true,
  enableSpotlightSwap: true,
  showFrameRate: false,
  enableCompanionMode: false,
  isPeerCall: false,
};

export const useConfigStore = create<ConfigState & Actions>()(
  subscribeWithSelector(
    combine(initialState, set => ({
      reset: () => set(initialState),
    })),
  ),
);

export const setConfig = (config: Partial<ConfigState>) => {
  useConfigStore.setState(config);
};
